import { PreviewFrame } from "./PreviewFrame";
import { CheckmarkCircle02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import cursorHand from "../assets/cursor-hand.png";

interface Task {
  title: string;
  due: string | null;
  dueColor: string;
  assignee: string;
  initials: string;
  avatarColor: string;
  state: "default" | "hover" | "completed";
}

const TASKS: Task[] = [
  {
    title: "Follow up with Angel Donin",
    due: null,
    dueColor: "transparent",
    assignee: "Mia Thompson",
    initials: "MT",
    avatarColor: "#00a7de",
    state: "hover"
  },
  {
    title: "Schedule tour for unit 4B",
    due: "Due yesterday",
    dueColor: "#de3c39",
    assignee: "Ethan Carter",
    initials: "EC",
    avatarColor: "#e6007e",
    state: "default"
  },
  {
    title: "Review application",
    due: "Due today",
    dueColor: "#de9306",
    assignee: "Sophie Reynolds",
    initials: "SR",
    avatarColor: "#52c535",
    state: "default"
  }
];

const Avatar = ({ initials, color }: { initials: string; color: string }) => (
  <span
    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[7px] font-black text-white"
    style={{ backgroundColor: color }}
    aria-hidden="true"
  >
    {initials}
  </span>
);

const Checkbox = ({ state }: { state: Task["state"] }) => {
  if (state === "hover")
    return (
      <HugeiconsIcon
        icon={CheckmarkCircle02Icon}
        size={20}
        className="text-content-300"
        strokeWidth={1.5}
      />
    );
  return (
    <span className="block h-5 w-5 rounded-full border-[1.5px] border-content-200" />
  );
};

export const TasksPreview = () => (
  <PreviewFrame>
    {/* Checkbox column — 52px (16px padding + 20px icon + 16px padding) */}
    <div className="flex w-[52px] shrink-0 flex-col border-r border-alpha-40">
      <div className="h-11" />
      {TASKS.map((task, idx) => (
        <div
          key={task.title}
          className="relative flex h-16 items-center justify-center border-t border-alpha-40"
        >
          <Checkbox state={task.state} />
          {idx === 0 && task.state === "hover" ? (
            <img
              src={cursorHand}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute"
              style={{
                left: "18.14px",
                top: "38.5px",
                width: "22.379px",
                height: "26.088px"
              }}
            />
          ) : null}
        </div>
      ))}
    </div>

    {/* Task column — flex-1 */}
    <div className="flex flex-1 flex-col">
      <div className="flex h-11 items-center px-4 py-3">
        <span className="text-[13px] font-medium leading-4 text-content-300">
          Task
        </span>
      </div>
      {TASKS.map((task) => (
        <div
          key={task.title}
          className="flex h-16 items-center border-t border-alpha-40 px-4 py-3"
        >
          <span className="truncate text-[14px] font-medium leading-5 text-content-1000">
            {task.title}
          </span>
        </div>
      ))}
    </div>

    {/* Due date column — 125px */}
    <div className="flex w-[125px] shrink-0 flex-col">
      <div className="flex h-11 items-center px-4 py-3">
        <span className="text-[13px] font-medium leading-4 text-content-300">
          Due date
        </span>
      </div>
      {TASKS.map((task) => (
        <div
          key={task.title}
          className="flex h-16 items-center border-t border-alpha-40 px-4 py-3"
        >
          <span
            className="text-[14px] font-medium leading-5"
            style={{ color: task.dueColor }}
          >
            {task.due ?? "—"}
          </span>
        </div>
      ))}
    </div>

    {/* Assigned to column — 172px */}
    <div className="flex w-[172px] shrink-0 flex-col">
      <div className="flex h-11 items-center py-3">
        <span className="text-[13px] font-medium leading-4 text-content-300">
          Assigned to
        </span>
      </div>
      {TASKS.map((task) => (
        <div
          key={task.title}
          className="flex h-16 items-center border-t border-alpha-40 py-4"
        >
          <div className="flex items-center gap-2 rounded-full bg-background-900 px-2 py-1">
            <Avatar initials={task.initials} color={task.avatarColor} />
            <span className="whitespace-nowrap text-[13px] font-medium leading-4 text-content-1000">
              {task.assignee}
            </span>
          </div>
        </div>
      ))}
    </div>
  </PreviewFrame>
);
